/**
 * Utility functions for transforming Air travel data
 */
import { getLocalDateFromEventDateString, getDateAndTimeFromIsoDateString } from './dateUtils';

/**
 * Utility function to perform necessary transformations needed for UI
 * @param {Object} airSetupData
 * @returns {Object}
 */
export function transformAirTravelData(airSetupData) {
  const updatedAirSetupData = { ...airSetupData };
  const { defaultSchedule, restrictions, rules = [] } = updatedAirSetupData;
  if (defaultSchedule) {
    defaultSchedule.departureDate = defaultSchedule.departureDate
      ? getLocalDateFromEventDateString(defaultSchedule.departureDate) : null;
    defaultSchedule.returnDate = defaultSchedule.returnDate
      ? getLocalDateFromEventDateString(defaultSchedule.returnDate) : null;
  }
  if (restrictions) {
    restrictions.earliestDepartureDate = restrictions.earliestDepartureDate
      ? getLocalDateFromEventDateString(restrictions.earliestDepartureDate) : null;
    restrictions.latestReturnDate = restrictions.latestReturnDate
      ? getLocalDateFromEventDateString(restrictions.latestReturnDate) : null;
  }
  if (rules.length > 0) {
    updatedAirSetupData.rules = rules.map(rule => ({
      ...rule,
      earliestDepartureDate: rule.earliestDepartureDate
        ? getLocalDateFromEventDateString(rule.earliestDepartureDate) : null,
      latestReturnDate: rule.latestReturnDate
        ? getLocalDateFromEventDateString(rule.latestReturnDate) : null,
      defaultDepartureDate: rule.defaultDepartureDate
        ? getLocalDateFromEventDateString(rule.defaultDepartureDate) : null,
      defaultReturnDate: rule.defaultReturnDate
        ? getLocalDateFromEventDateString(rule.defaultReturnDate) : null
    }));
  }
  return updatedAirSetupData;
}

const populateOvernightFlag = (groupFlightData, departDateOnlyString, arrivalDateOnlyString) => {
  return {
    ...groupFlightData,
    // Dates not being equal is considered overnight; Yes, we had a discussion with the PO on this
    isOvernight: departDateOnlyString !== arrivalDateOnlyString
  };
};

const transformGroupFlightLegsData = (groupFlightLegs) => {
  let modifiedGroupFlightLegs = [];
  Object.keys(groupFlightLegs).forEach(legId => {
    const groupFlightLeg = groupFlightLegs[legId];
    const { departDate, arriveDate } = groupFlightLeg;
    const departureDateAndTime = getDateAndTimeFromIsoDateString(departDate);
    const arrivalDateAndTime = getDateAndTimeFromIsoDateString(arriveDate);

    const modifiedLegData = {
      ...groupFlightLeg,
      departDate: getLocalDateFromEventDateString(departureDateAndTime.dateString),
      departTime: departureDateAndTime.timeString,
      arriveDate: getLocalDateFromEventDateString(arrivalDateAndTime.dateString),
      arriveTime: arrivalDateAndTime.timeString
    };
    modifiedGroupFlightLegs.push(populateOvernightFlag(modifiedLegData,
      departureDateAndTime.dateString, arrivalDateAndTime.dateString));
  });
  return modifiedGroupFlightLegs;
};

export const populateCapacityForGroupFlights = (groupFlightCapacityIds, capacityMap, isPlanner) => {
  let capacityMapToReturn = { ...capacityMap };
  if (isPlanner) {
    groupFlightCapacityIds.forEach(id => {
      capacityMapToReturn[id] = {
        id,
        availableCapacity: -1,
        hasCapacity: true
      };
    });
  } else if (capacityMapToReturn && !isPlanner) {
    groupFlightCapacityIds.forEach(id => {
      capacityMapToReturn[id] = {
        ...capacityMapToReturn[id],
        hasCapacity: capacityMapToReturn[id].availableCapacity > 0
          || capacityMapToReturn[id].totalCapacityAvailable === -1
      };
    });
  }
  return capacityMapToReturn;
};

export const getCapacityIdBasedOnTestMode = (id, testMode) => `${id}${testMode ? '_testmode' : ''}`;

export const transformGroupFlightsData = (groupFlightSetup, isInTestMode, isGroupFlightEnabled) => {
  if (!isGroupFlightEnabled) {
    return {};
  }
  const { groupFlights } = groupFlightSetup;
  let modifiedGroupFlights = [];
  Object.keys(groupFlights).forEach(id => {
    const groupFlight = groupFlights[id];
    const { departDate, arriveDate, groupFlightLegs } = groupFlight;
    const departureDateAndTime = getDateAndTimeFromIsoDateString(departDate);
    const arrivalDateAndTime = getDateAndTimeFromIsoDateString(arriveDate);

    const modifiedGroupFlight = {
      ...groupFlight,
      capacityId: getCapacityIdBasedOnTestMode(groupFlight.id, isInTestMode),
      departDate: getLocalDateFromEventDateString(departureDateAndTime.dateString),
      departTime: departureDateAndTime.timeString,
      arriveDate: getLocalDateFromEventDateString(arrivalDateAndTime.dateString),
      arriveTime: arrivalDateAndTime.timeString,
      groupFlightLegs: transformGroupFlightLegsData(groupFlightLegs)
    };
    modifiedGroupFlights.push(populateOvernightFlag(modifiedGroupFlight,
      departureDateAndTime.dateString, arrivalDateAndTime.dateString));
  });
  return {
    ...groupFlightSetup,
    groupFlights: modifiedGroupFlights
  };
};
